// node modules
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import withStyles from 'isomorphic-style-loader/withStyles';
import Cookies from 'js-cookie';

// app modules: props
import LoginProps from 'components/Login/LoginProps';
import LayoutProps from 'components/Layout/LayoutProps';

// app modules: components
import PaddleJS from 'components/PaddleJS';

// app modules: services
import { getGoogleAnalyticsClientId } from 'services/analytics';

// assets
import { BREAKPOINTS } from 'constants/index';
import CssPaddleCheckout from './PaddleCheckout.scss';

class PaddleCheckout extends Component {
  static propTypes = {
    productId: PropTypes.number,
    override: PropTypes.string,
    coupon: PropTypes.string,
    user: LoginProps.isRequired,
    environment: PropTypes.string.isRequired,
    dimensions: LayoutProps.dimensions.isRequired,
    onCheckoutSuccess: PropTypes.func,
    paddleLoaded: PropTypes.bool,
    postcode: PropTypes.string,
    country: PropTypes.string,
    theme: PropTypes.string.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    extraPassthrough: PropTypes.object,
  };

  static defaultProps = {
    productId: null,
    override: null,
    coupon: null,
    paddleLoaded: false,
    onCheckoutSuccess: () => {},
    postcode: undefined,
    country: undefined,
    extraPassthrough: {},
  };

  constructor(props) {
    super(props);

    this.bound = {
      checkoutSuccess: this.checkoutSuccess.bind(this),
    };
  }

  componentDidMount() {
    this.displayCheckoutOverlay();
  }

  shouldComponentUpdate(nextProps) {
    // An update will require the checkout to reload.
    // To avoid accidentally aborting, we will be very strict on what can cause an update.

    // Paddle JS loading
    if (!this.props.paddleLoaded && nextProps.paddleLoaded) {
      return true;
    }
    // Changing the product will unfortunately cause a reload - we'll have to live with that.
    if (this.props.productId !== nextProps.productId) {
      return true;
    }

    // User loading (or changing)
    if (this.props.user.id !== nextProps.user.id) {
      return true;
    }

    // Discovering screen size is allowed
    if (this.props.dimensions.body !== nextProps.dimensions.body) {
      return true;
    }

    // override param
    if (this.props.override !== nextProps.override) {
      return true;
    }

    // Ignore everything else
    // Most importantly, this will avoid the loop where setting a coupon code
    // in the checkout causes the checkout to be re-loaded
    return false;
  }

  componentDidUpdate(prevProps) {
    const wasPhone = prevProps.dimensions.body < BREAKPOINTS.PHONE_VERTICAL;
    const isPhone = this.props.dimensions.body < BREAKPOINTS.PHONE_VERTICAL;
    if (
      prevProps.productId !== this.props.productId ||
      prevProps.coupon !== this.props.coupon ||
      wasPhone !== isPhone ||
      prevProps.paddleLoaded !== this.props.paddleLoaded ||
      (prevProps.user.id === -1 && this.props.user.id > 0)
    ) {
      this.displayCheckoutOverlay();
    }
  }

  checkoutSuccess(checkout) {
    const { onCheckoutSuccess } = this.props;
    // The paddle JS library still interacts with the iframe after checkout success fires,
    // so delay any action that would remove it from the DOM.
    setTimeout(() => onCheckoutSuccess(checkout), 1000);
  }

  async displayCheckoutOverlay() {
    const {
      productId,
      coupon,
      user,
      environment,
      dimensions,
      paddleLoaded,
      country,
      postcode,
      extraPassthrough,
      override,
      urlQueryString,
      theme,
      ...others
    } = this.props;
    const { email, id } = user;
    const gaClientId = await getGoogleAnalyticsClientId();
    const passthrough = {
      ...extraPassthrough,
      userId: id,
      environment,
      gaClientId,
      // Required for Facebook conversion tracking
      fbp: Cookies.get('_fbp'),
      client_user_agent: navigator.userAgent,
      event_source_url: window.location.href.split('?')[0],
      // End Facebook conversion tracking
    };
    if (urlQueryString) {
      if (urlQueryString?.length < 500) {
        passthrough.q = urlQueryString;
      } else {
        passthrough.warn = 'qs-too-long';
      }
    }

    const frameStyle = 'width: 100%; border: none; background: transparent; padding: 0;';
    if (paddleLoaded && user.id > 0) {
      const checkoutParams = {
        displayModeTheme: theme,
        method: 'inline',
        disableLogout: true,
        successCallback: this.bound.checkoutSuccess,
        allowQuantity: false,
        frameTarget: 'paddle-checkout',
        frameInitialHeight: 533,
        frameStyle,
        ...others,
      };
      if (override) {
        checkoutParams.override = override;
      } else {
        checkoutParams.postcode = postcode;
        checkoutParams.country = country;
        checkoutParams.coupon = coupon;
        checkoutParams.passthrough = passthrough;
        checkoutParams.email = email;
        checkoutParams.product = productId;
      }

      window.Paddle.Checkout.open(checkoutParams);
    }
  }

  render() {
    const { paddleLoaded, theme } = this.props;
    return (
      <div className="paddle-checkout__wrapper">
        {!paddleLoaded ? <PaddleJS /> : null}
        <div className="paddle-checkout" data-theme={theme} />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  user: state.user,
  environment: state.config.environment,
  dimensions: state.dimensions,
  paddleLoaded: state.paywall.paddleLoaded,
  postcode: state.paywall.postcode,
  country: state.paywall.country,
  urlQueryString: state.paywall.arrivalQueryString,
  theme: state.config.theme,
});

const mapDispatchToProps = {};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(CssPaddleCheckout)(PaddleCheckout));
